import * as React from "react";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import styled from "@mui/material/styles/styled";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch, useAppSelector } from "app/hooks";
import AndroidQR from "assets/icons/ios/new-android-app-qr.svg";
import IPhoneQR from "assets/icons/ios/new-ios-app-qr.svg";
import IPhoneAppPreview from "assets/icons/ios/new-iphone-app-preview.png";
import { SunoLogo } from "components/icons";

import {
  clearState,
  selectAuthIsInitialLoad,
  selectAuthIsSuccess,
  selectCurrentUserToken
} from "../auth/auth-slice";

type LocationProps = {
  state: {
    from: Location;
  };
};

const StyledBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "48px 24px",
  maxWidth: 720,
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  gap: 24
}));

export default function UnauthorizedLayout() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation() as unknown as LocationProps;
  const navigate = useNavigate();
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const isLogged = useAppSelector(selectCurrentUserToken);
  const isInitialLoad = useAppSelector(selectAuthIsInitialLoad);
  useEffect(() => {
    return () => {
      if (isInitialLoad) {
        dispatch(clearState());
      }
    };
  }, [dispatch, isInitialLoad]);

  useEffect(() => {
    if (isSuccess && Boolean(isLogged)) {
      dispatch(clearState());
      if (location.state?.from) {
        navigate(location.state?.from, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isSuccess, isLogged, dispatch, navigate, location.state]);

  return (
    <Grid container minHeight="100dvh" columns={{ xs: 6, md: 12 }}>
      <Grid
        item
        xs={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100dvh"
      >
        <Stack
          gap={5}
          m="auto"
          maxWidth={400}
          width="100%"
          alignItems="center"
          sx={{ form: { width: "100%" } }}
        >
          <Box sx={{ "& g": { fill: "black" } }}>
            <SunoLogo width={180} height={52} />
          </Box>

          <Outlet />
        </Stack>
      </Grid>
      <Grid
        display={{ xs: "none", md: "flex" }}
        item
        xs={6}
        bgcolor={theme.palette.sideMenu.main}
        minHeight="100dvh"
        flexDirection="column"
        position="relative"
      >
        <StyledBox gap={2} height={586} mx="auto" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            alignItems="center"
            width="100%"
          >
            <Typography
              color={theme.palette.common.white}
              pb={3}
              fontSize={44}
              fontWeight={700}
              lineHeight={1.2}
            >
              Download Our New&nbsp;Mobile&nbsp;App
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              width="94%"
            >
              <Box
                component="img"
                height={{ xs: "70%", lg: "100%" }}
                alt="IPhone App Preview"
                src={IPhoneAppPreview}
              />

              <Stack width={{ xs: 200, lg: 250 }} gap={9} alignItems="center">
                <Stack>
                  <Box
                    component="a"
                    href="https://apps.apple.com/us/app/suno-software/id6448741301"
                    target="_blank"
                  >
                    <Box
                      maxWidth="100%"
                      component="img"
                      alt="App Store QR code"
                      src={IPhoneQR}
                    />
                  </Box>
                  <Box
                    component="a"
                    href="https://apps.apple.com/us/app/suno-software/id6448741301"
                    color="white"
                    textAlign="center"
                    mt={-1.5}
                    target="_blank"
                  >
                    Get iOS App
                  </Box>
                </Stack>

                <Stack>
                  <Box
                    component="a"
                    href="https://tinyurl.com/ycxfsnpz"
                    target="_blank"
                  >
                    <Box
                      maxWidth="100%"
                      component="img"
                      alt="Android App QR code"
                      src={AndroidQR}
                    />
                  </Box>
                  <Box
                    component="a"
                    href="https://tinyurl.com/ycxfsnpz"
                    color="white"
                    textAlign="center"
                    mt={-1.5}
                    target="_blank"
                  >
                    Get Android App
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </StyledBox>
        <Box
          position="absolute"
          bottom={0}
          left="50%"
          maxWidth={720}
          width="100%"
          pb={2}
          sx={{ transform: "translateX(-50%)" }}
        >
          <Typography
            ml="auto"
            color={theme.palette.common.white}
            pr={8}
            display="block"
            width="fit-content"
          >
            Copyright © {new Date().getFullYear()} Suno Inc
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
